import React, { useState } from 'react';
import {
  employeesAPI,
  useEmployees,
  useBranches,
  useDepartments,
  useCreateEmployee,
  useUpdateEmployee,
  useDeleteEmployee,
} from '@/api';
import { storageUrl } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { useBranchScope } from '@/context/BranchScopeContext';
import { Card, CardContent, Button, Input, Badge, Modal } from '@/components/ui';
import { LoadingSpinner, Pagination } from '@/components/common';
import { Trash2, Edit2, Plus, Download } from 'lucide-react';
import { extractPaginatedData } from '@/utils/pagination';
import { useDebouncedValue } from '@/utils/useDebouncedValue';
import { downloadBlob } from '@/utils/download';
import { toast } from 'react-toastify';

export default function EmployeesPage() {
  const perPage = 10;
  const { can } = useAuth();
  const canManage = can('manage_employees');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<any>(null);
  const debouncedSearch = useDebouncedValue(search.trim());
  const { branchParam } = useBranchScope();
  const { data: employeesData, isLoading, isError, error } = useEmployees({
    page,
    per_page: perPage,
    search: debouncedSearch,
    branch_id: branchParam,
    include_inactive: true,
  });
  const { data: branchesData } = useBranches({ per_page: 100 });
  const { data: departmentsData } = useDepartments({ per_page: 100 });
  const createMutation = useCreateEmployee();
  const updateMutation = useUpdateEmployee();
  const deleteMutation = useDeleteEmployee();

  const { items: employees, meta } = extractPaginatedData<any>(employeesData, page, perPage);

  const branches = Array.isArray(branchesData?.data)
    ? branchesData.data
    : branchesData?.data?.data || [];

  const departments = Array.isArray(departmentsData?.data)
    ? departmentsData.data
    : departmentsData?.data?.data || [];

  const handleDelete = async (id: number) => {
    if (window.confirm('Вы уверены?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await employeesAPI.export({
        search: search.trim(),
        include_inactive: true,
      });

      downloadBlob(blob, `employees_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Реестр сотрудников выгружен в Excel');
    } catch (error: any) {
      toast.error(error?.message || 'Не удалось выгрузить сотрудников');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Сотрудники</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            Экспорт в Excel
          </Button>
          {canManage && (
            <Button onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Добавить сотрудника
            </Button>
          )}
        </div>
      </div>

      <Modal
        isOpen={showForm || !!editingEmployee}
        onClose={() => {
          setShowForm(false);
          setEditingEmployee(null);
        }}
        title={editingEmployee ? 'Редактировать сотрудника' : 'Новый сотрудник'}
      >
        <EmployeeForm
          initialData={editingEmployee}
          branches={branches}
          departments={departments}
          onSubmit={async (data: any) => {
            if (editingEmployee) {
              await updateMutation.mutateAsync({ id: editingEmployee.id, data });
            } else {
              await createMutation.mutateAsync(data);
            }
            setShowForm(false);
            setEditingEmployee(null);
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingEmployee(null);
          }}
        />
      </Modal>

      {/* Search */}
      <div>
        <Input
          type="search"
          name="employee_search"
          placeholder="Поиск по имени, ID или email..."
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          autoComplete="off"
          data-lpignore="true"
        />
      </div>

      {isError && (
        <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
          {(error as any)?.message || 'Не удалось загрузить сотрудников. Проверьте права доступа и API.'}
        </div>
      )}

      {/* Employees Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto ui-table-wrap">
            <table className="w-full ui-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">ID</th>
                  <th className="text-left py-3 px-4 font-semibold">Имя</th>
                  <th className="text-left py-3 px-4 font-semibold">Телефон</th>
                  <th className="text-left py-3 px-4 font-semibold">Email</th>
                  <th className="text-left py-3 px-4 font-semibold">Отдел</th>
                  <th className="text-left py-3 px-4 font-semibold">Статус</th>
                  <th className="text-left py-3 px-4 font-semibold">Действия</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp: any) => (
                  <tr key={emp.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">{emp.employee_id}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {emp.photo_path && (
                          <img
                            src={storageUrl(emp.photo_path)!}
                            alt={emp.full_name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        )}
                        {emp.full_name}
                      </div>
                    </td>
                    <td className="py-3 px-4">{emp.phone || '-'}</td>
                    <td className="py-3 px-4">{emp.email || '-'}</td>
                    <td className="py-3 px-4">{emp.department?.name || '-'}</td>
                    <td className="py-3 px-4">
                      <Badge variant={emp.is_active ? 'default' : 'secondary'}>
                        {emp.is_active ? 'Активен' : 'Неактивен'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      {canManage && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingEmployee(emp)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(emp.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Pagination meta={meta} onPageChange={setPage} />
      {employees.length === 0 && (
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900">Сотрудники не найдены</h3>
          <p className="text-gray-500">Создайте нового сотрудника для начала работы</p>
        </div>
      )}
    </div>
  );
}

// Employee Form Component
function EmployeeForm({ initialData, branches, departments, onSubmit, onCancel }: any) {
  // Sanani "2026-04-15T..." formatdan "2026-04-15" ga keltirish
  const toDateInput = (val: string | null | undefined) => {
    if (!val) return '';
    return val.split('T')[0];
  };

  const [formData, setFormData] = useState({
    full_name: initialData?.full_name || '',
    email: initialData?.email || '',
    employee_id: initialData?.employee_id || '',
    phone: initialData?.phone || '',
    telegram_id: initialData?.telegram_id || '',
    branch_id: initialData?.branch_id || '',
    department_id: initialData?.department_id || '',
    hire_date: toDateInput(initialData?.hire_date) || new Date().toISOString().split('T')[0],
    date_of_birth: toDateInput(initialData?.date_of_birth),
    is_active: initialData?.is_active ?? true,
    is_schedule_exempt: initialData?.is_schedule_exempt ?? false,
    exempt_reason: initialData?.exempt_reason || '',
    is_penalty_exempt: initialData?.is_penalty_exempt ?? false,
    penalty_exempt_reason: initialData?.penalty_exempt_reason || '',
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(storageUrl(initialData?.photo_path) || null);
  const [removePhoto, setRemovePhoto] = useState(false);

  const filteredDepartments = departments.filter(
    (dep: any) => !formData.branch_id || String(dep.branch_id) === String(formData.branch_id)
  );

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setPhotoFile(file);
    setRemovePhoto(false);
    if (file) {
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setRemovePhoto(true);
  };

  const handleSubmit = () => {
    if (photoFile || removePhoto) {
      const fd = new FormData();
      Object.entries(formData).forEach(([key, val]) => {
        if (key === 'is_active' || key === 'is_schedule_exempt' || key === 'is_penalty_exempt') {
          fd.append(key, val ? '1' : '0');
        } else {
          fd.append(key, String(val ?? ''));
        }
      });
      if (photoFile) {
        fd.append('photo', photoFile);
      } else if (removePhoto) {
        fd.append('remove_photo', '1');
      }
      onSubmit(fd);
    } else {
      onSubmit(formData);
    }
  };

  return (
    <div className="space-y-4">
      {/* Photo upload */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-gray-100 border flex items-center justify-center overflow-hidden shrink-0 relative">
          {photoPreview
            ? <img src={photoPreview} alt="photo" className="w-full h-full object-cover" />
            : <span className="text-2xl text-gray-400">👤</span>
          }
        </div>
        <div className="flex-1 space-y-1">
          <label className="block text-sm font-medium text-gray-700">Фото сотрудника</label>
          <input
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
          />
          {photoPreview && (
            <button
              type="button"
              onClick={handleRemovePhoto}
              className="text-xs text-red-500 hover:text-red-700 underline"
            >
              Фотони ўчириш
            </button>
          )}
        </div>
      </div>

      {/* Branch & Department */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Филиал *</label>
          <select
            className="w-full px-3 py-2 border rounded-lg text-sm"
            value={formData.branch_id}
            onChange={(e) => setFormData({ ...formData, branch_id: e.target.value, department_id: '' })}
          >
            <option value="">Выберите филиал</option>
            {branches.map((branch: any) => (
              <option key={branch.id} value={branch.id}>{branch.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Отдел *</label>
          <select
            className="w-full px-3 py-2 border rounded-lg text-sm"
            value={formData.department_id}
            onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
          >
            <option value="">Выберите отдел</option>
            {filteredDepartments.map((dep: any) => (
              <option key={dep.id} value={dep.id}>{dep.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Name & Employee ID */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Полное имя *</label>
          <Input
            placeholder="Иван Иванов"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">ID сотрудника *</label>
          <Input
            placeholder="EMP001"
            value={formData.employee_id}
            onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
          />
        </div>
      </div>

      {/* Phone & Telegram */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Телефон</label>
          <Input
            placeholder="+998901234567"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Telegram ID</label>
          <Input
            placeholder="@username"
            value={formData.telegram_id}
            onChange={(e) => setFormData({ ...formData, telegram_id: e.target.value })}
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Email</label>
        <Input
          type="email"
          placeholder="example@company.com"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Дата приема *</label>
          <Input
            type="date"
            value={formData.hire_date}
            onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Дата рождения</label>
          <Input
            type="date"
            value={formData.date_of_birth}
            onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
          />
        </div>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="text-sm text-gray-700">Активный сотрудник</span>
      </label>

      {/* Erkin grafik */}
      <div className="border rounded-lg p-3 space-y-2 bg-amber-50 border-amber-200">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_schedule_exempt}
            onChange={(e) => setFormData({ ...formData, is_schedule_exempt: e.target.checked, exempt_reason: e.target.checked ? formData.exempt_reason : '' })}
            className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
          />
          <span className="text-sm font-medium text-amber-800">🕐 Erkin grafik (jadval tekshirilmaydi)</span>
        </label>
        {formData.is_schedule_exempt && (
          <div>
            <label className="block text-xs text-amber-700 mb-1">Sabab (ixtiyoriy)</label>
            <Input
              placeholder="Masalan: Rahbariyat, Sotuvchi, Masofaviy ish..."
              value={formData.exempt_reason}
              onChange={(e) => setFormData({ ...formData, exempt_reason: e.target.value })}
            />
          </div>
        )}
      </div>

      {/* Jarimadan ozod */}
      <div className="border rounded-lg p-3 space-y-2 bg-green-50 border-green-200">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_penalty_exempt}
            onChange={(e) => setFormData({ 
              ...formData, 
              is_penalty_exempt: e.target.checked, 
              penalty_exempt_reason: e.target.checked ? formData.penalty_exempt_reason : '' 
            })}
            className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            disabled={formData.is_schedule_exempt}
          />
          <span className="text-sm font-medium text-green-800">
            💚 Jarimadan ozod (davomat yoziladi, jarima yozilmaydi)
          </span>
        </label>
        {formData.is_schedule_exempt && (
          <p className="text-xs text-amber-600">
            ⚠️ Erkin grafikda avtomatik jarimadan ozod bo'ladi
          </p>
        )}
        {formData.is_penalty_exempt && !formData.is_schedule_exempt && (
          <div>
            <label className="block text-xs text-green-700 mb-1">Sabab (ixtiyoriy)</label>
            <Input
              placeholder="Masalan: Direktor, Menejer, Maxsus shartnoma..."
              value={formData.penalty_exempt_reason}
              onChange={(e) => setFormData({ ...formData, penalty_exempt_reason: e.target.value })}
            />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={handleSubmit} className="flex-1">
          Сохранить
        </Button>
        <Button onClick={onCancel} variant="outline" className="flex-1">
          Отмена
        </Button>
      </div>
    </div>
  );
}


